import type { Quad } from "./geometry";

/** Longest side of a finished scan. 3000 px across a 6×4" print is 500 dpi,
 *  more than a phone camera actually resolves, while keeping each RGBA
 *  buffer under ~36 MB on low-end phones. */
export const MAX_SCAN_SIZE = 3000;

/**
 * Cut the print out of `src` and straighten it into a rectangle: a
 * perspective (homography) warp with bilinear sampling.
 */
export function warpQuad(src: ImageData, quad: Quad): ImageData {
  const sw = src.width;
  const sh = src.height;
  const P = [quad.tl, quad.tr, quad.br, quad.bl].map((p) => ({
    x: p.x * (sw - 1),
    y: p.y * (sh - 1),
  }));
  const len = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  let outW = Math.max(len(P[0]!, P[1]!), len(P[3]!, P[2]!));
  let outH = Math.max(len(P[0]!, P[3]!), len(P[1]!, P[2]!));
  const scale = Math.min(1, MAX_SCAN_SIZE / Math.max(outW, outH));
  outW = Math.max(1, Math.round(outW * scale));
  outH = Math.max(1, Math.round(outH * scale));

  // Maps output pixel (u, v) to source pixel (x, y).
  const H = homography(
    [
      [0, 0],
      [outW - 1, 0],
      [outW - 1, outH - 1],
      [0, outH - 1],
    ],
    P.map((p) => [p.x, p.y] as [number, number]),
  );

  const out = new ImageData(outW, outH);
  const s = src.data;
  const o = out.data;
  const [h0, h1, h2, h3, h4, h5, h6, h7] = H;
  for (let v = 0; v < outH; v++) {
    for (let u = 0; u < outW; u++) {
      const den = h6 * u + h7 * v + 1;
      let x = (h0 * u + h1 * v + h2) / den;
      let y = (h3 * u + h4 * v + h5) / den;
      if (x < 0) x = 0;
      else if (x > sw - 1) x = sw - 1;
      if (y < 0) y = 0;
      else if (y > sh - 1) y = sh - 1;
      const x0 = x | 0;
      const y0 = y | 0;
      const x1 = x0 < sw - 1 ? x0 + 1 : x0;
      const y1 = y0 < sh - 1 ? y0 + 1 : y0;
      const fx = x - x0;
      const fy = y - y0;
      const i00 = (y0 * sw + x0) * 4;
      const i10 = (y0 * sw + x1) * 4;
      const i01 = (y1 * sw + x0) * 4;
      const i11 = (y1 * sw + x1) * 4;
      const oi = (v * outW + u) * 4;
      for (let c = 0; c < 3; c++) {
        const top = s[i00 + c]! + (s[i10 + c]! - s[i00 + c]!) * fx;
        const bot = s[i01 + c]! + (s[i11 + c]! - s[i01 + c]!) * fx;
        o[oi + c] = top + (bot - top) * fy;
      }
      o[oi + 3] = 255;
    }
  }
  return out;
}

type H8 = [number, number, number, number, number, number, number, number];

/** Solve for the 8 coefficients mapping each `from` point onto `to`. */
function homography(
  from: [number, number][],
  to: [number, number][],
): H8 {
  const A: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const [u, v] = from[i]!;
    const [x, y] = to[i]!;
    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x, x]);
    A.push([0, 0, 0, u, v, 1, -u * y, -v * y, y]);
  }
  // Gaussian elimination with partial pivoting on the 8×9 augmented matrix.
  for (let col = 0; col < 8; col++) {
    let pivot = col;
    for (let r = col + 1; r < 8; r++) {
      if (Math.abs(A[r]![col]!) > Math.abs(A[pivot]![col]!)) pivot = r;
    }
    [A[col], A[pivot]] = [A[pivot]!, A[col]!];
    const pv = A[col]![col]!;
    if (Math.abs(pv) < 1e-12) throw new Error("Corners are degenerate");
    for (let c = col; c < 9; c++) A[col]![c]! /= pv;
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const f = A[r]![col]!;
      if (f === 0) continue;
      for (let c = col; c < 9; c++) A[r]![c]! -= f * A[col]![c]!;
    }
  }
  return A.map((row) => row[8]!) as H8;
}
